import {Component, Input, OnInit} from "@angular/core";
import {HomeService, IHomeObject } from "../../../services/home.service";
import {Config} from "src/app/services/utils/Config";
import {HomeDeviceService} from "../../../services/home-device.service";
import {ILockObject, LockCallback, LockService} from "../../../services/lock.service";
import {UPDATE_DEVICES, UPDATE_LOCKS} from "../../../services/utils/state-management/actions";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../services/utils/state-management/store";
import {toSecondsOfDay, Utility} from "../../../services/utils/utility";
import { LanguageChangeService } from "src/app/services/language-change.service";

@Component({
  selector: "app-home-devices",
  templateUrl: "./home-devices.component.html",
  styleUrls: ["./home-devices.component.css"]
})
export class HomeDevicesComponent implements OnInit, LockCallback {
  @Input() homeID: string;
  @Input() member: boolean;
  @Input() viewType: string;
  homeDevices = [];
  home: IHomeObject;
  homeLocks: ILockObject[];
  accessibleLocks: boolean;
  windowAccess: boolean;
  homeName: string;
  bleAdvertisement = '1';
  batteryLevel = 20;
  lockState = 'jammed';
  isLoaderActive = false;
  SECONDS_PER_DAY = 86400;
  currentLang: string
  activation_date = "activationdate";
  accessNotStarted: boolean;

  constructor(
    private homeService: HomeService,
    private homeDeviceService: HomeDeviceService,
    private ngRedux: NgRedux<IAppState>,
    private utility: Utility,
    private lockService: LockService,
    private langService: LanguageChangeService,
  ) {}

  ngOnInit() {
    this.homeLocks = null;
    this.accessibleLocks = null;
    this.windowAccess = null;

    // Fetch devices first
    this.getDevices(this.homeID);

    // Ensure home data is available before using it
    const homeData = this.homeService.getHomeById(this.homeID);
    if (homeData) {
      this.homeName = homeData.homeName;
      this.home = homeData;
    } else {
      console.warn('Home data not found for ID:', this.homeID);
    }

    // Fetch locks and trigger callback
    this.lockService.getLocksByHomeId(this.homeID, this);
  }

  callback(lock: ILockObject[]) {
    if (lock == null) {
      this.homeLocks = [];
    } else {
      this.homeLocks = lock;

      // Ensure home data is available
      if (!this.home) {
        this.home = this.homeService.getHomeById(this.homeID);
      }

      // Proceed only if accessTime is available
      if (this.home?.accessTime) {
        this.accessNotStarted = this.checkAccessNotStarted(this.home.accessTime);
        this.accessibleLocks = this.checkLockAccess(this.home.accessTime);
        this.windowAccess = this.checkWindowAccess(this.home.accessTime);
      } else {
        console.warn('Access time not found for home:', this.homeID);
        this.accessibleLocks = true;
        this.windowAccess = true;
      }

      this.currentLang = this.langService.getCurrentLanguage();
    }
  }

  getDevices(homeId: string) {
    this.homeDeviceService.getDevicesByHomeId(homeId).subscribe({
      next: (res: { halo: any; havasu: any; }) => {
        if (res?.halo?.haloDevices && res?.havasu?.havDevices) {
          const lock: ILockObject[] = res.halo.haloDevices.map(element => ({
            lockState: element['lockstatus']?.toLowerCase() || '',
            lockName: element['devicename'],
            firmwareVersion: element['firmwareversion'],
            lockBattery: element['batterypercentage'],
            deviceID: element['deviceid'],
            modelNumber: element['modelnumber'],
            batteryStatus: element['batterystatus'],
            isBLE: element['bleadvertisement'] === '1' || element['modelnumber'].includes('BLE'),
            bleAdvertisement: element['bleadvertisement'],
            lastUpdateStatus: element['lastupdatestatus'],
            hardwareVariant: element['hardwarevariant'],
            activationdate: element['activationdate'],
          }));

          this.homeDevices.push(...lock);
          this.utility.sort(lock, this.activation_date, "date");
          this.ngRedux.dispatch({ type: UPDATE_LOCKS, homeID: homeId, locks: lock });

          const gdoDevices = res.havasu.havDevices.map(element => ({
            ...element,
            activationdate: element['activationDate'],
          }));

          this.homeDevices.push(...gdoDevices);
          this.utility.sort(this.homeDevices, this.activation_date, "date");
          this.ngRedux.dispatch({ type: UPDATE_DEVICES, homeID: homeId, devices: this.homeDevices });
        }
      },
      error: (err: any) => {
        console.error('Error fetching gets:', err);
      },
    });
  }

  checkUpdateLastStatus(lastUpdateStatus: number) {
    let currentDate = Math.floor(Date.now() / 1000);
    let lockStatus = currentDate - lastUpdateStatus;
    return lockStatus <= Config.UPDATE_STATUS_SECONDS;
  }

  checkDeviceType(device: any) {
    if (device.hasOwnProperty('lockState')) {
      return 'lock';
    } else if (device.hasOwnProperty('manualMatterCode')) {
      return 'gdo';
    }
  }

  checkAccessNotStarted(accessTime: any): boolean {
    if (!accessTime || Object.keys(this.home?.accessTime).length === 0) return false;
    const now = Math.floor(Date.now() / 1000);
    const type = accessTime.accesstimetype;
    if (type === 'DATE_RANGE') {
      return now < Number(accessTime.startdate);
    }
    return now < Number(accessTime.starttime);
  }

  checkLockAccess(accessTime: any): boolean {
    if (!accessTime || Object.keys(this.home?.accessTime).length === 0) return true;
    const now = Math.floor(Date.now() / 1000);
    const type = accessTime.accesstimetype;
    if (type === 'DATE_RANGE') {
      const start = Number(accessTime.startdate);
      const end = Number(accessTime.enddate);
      return now >= start && now <= end;
    }
    const start = Number(accessTime.starttime);
    const end = Number(accessTime.endtime);
    return now >= start && now <= end;
  }

  checkWindowAccess(accessTime: { starttime: string, endtime: string, days: string[], accesstimetype: string }): boolean {
    if (!accessTime || accessTime.accesstimetype == "DATE_RANGE" || Object.keys(this.home?.accessTime).length === 0) return true;
    const { starttime, endtime, days } = accessTime;
    if (days?.length) {
      const today = new Date().toLocaleString('en-us', { weekday: 'short' });
      if (!days.includes(today)) return false;
    }
    const now = new Date();
    const nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    let startSecs = Number(starttime);
    let endSecs = Number(endtime);
    // Normalize if using full timestamps instead of seconds of day
    if (startSecs > this.SECONDS_PER_DAY) startSecs = toSecondsOfDay(startSecs);
    if (endSecs > this.SECONDS_PER_DAY) endSecs = toSecondsOfDay(endSecs);
    return startSecs <= endSecs
      ? nowSeconds >= startSecs && nowSeconds <= endSecs
      : nowSeconds >= startSecs || nowSeconds <= endSecs;
  }

  getTranslatedDays(days: string[]): string[] {
    if (!days?.length) return [];
    const formatter = new Intl.DateTimeFormat(this.currentLang, { weekday: 'short' });

    // Generate a map of 'Sun'...'Sat' to their respective weekday indices
    const dayAbbrToIndex = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(Date.UTC(2023, 0, 1 + i)); // Jan 1, 2023 is a Sunday
      const abbr = formatter.format(date).replace(/\.$/, '');
      return [abbr, i] as [string, number];
    }).reduce((map, [abbr, i]) => {
      map[abbr.toString().slice(0, 3)] = i;
      return map;
    }, {} as Record<string, number>);

    return days.map(code => {
      const index = dayAbbrToIndex[code];
      if (index === undefined) return code;
      const date = new Date(Date.UTC(2023, 0, 1 + index));
      const label = formatter.format(date).replace(/\\.$/, '');
      return label.charAt(0).toUpperCase() + label.slice(1);
    });
  }
}
